import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CONSENT_KEY, initAnalytics } from "../lib/analytics";

/**
 * CookieConsent — lightweight, cookieless-aware consent banner.
 *
 * Strictly-necessary auth/routing cookies always run; non-essential product
 * analytics (PostHog) stays off until the visitor accepts. Choice is stored in
 * localStorage so the banner shows only until a decision is made.
 */
export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const choice = window.localStorage.getItem(CONSENT_KEY);
      if (choice !== "granted" && choice !== "denied") setShow(true);
    } catch {
      /* private mode — don't block the app */
    }
  }, []);

  const decide = (granted) => {
    try {
      window.localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
    } catch {
      /* ignore */
    }
    if (granted) initAnalytics();
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-4 md:p-5" data-testid="cookie-consent">
      <div className="mx-auto flex max-w-[820px] flex-col gap-4 rounded-2xl border-2 border-ink bg-white p-5 shadow-sticker-lg md:flex-row md:items-center">
        <p className="flex-1 font-sans text-sm text-ink/90">
          We use essential cookies to run LinkLane and, with your consent, cookieless analytics to
          improve the product. See our{" "}
          <Link to="/privacy" className="text-candy underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            onClick={() => decide(false)}
            className="rounded-xl border-2 border-ink bg-white px-4 py-2 font-display text-sm font-bold transition-colors hover:bg-paper"
            data-testid="cookie-decline"
          >
            Decline
          </button>
          <button
            onClick={() => decide(true)}
            className="rounded-xl border-2 border-ink bg-candy px-4 py-2 font-display text-sm font-bold text-white shadow-sticker-xs"
            data-testid="cookie-accept"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
